import { useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';

import { useTabbar } from '@common/providers/useTabbar';
import { BottomSheet } from '@common/ui/BottomSheet';
import { Button } from '@common/ui/buttons/Button';
import { Card } from '@common/ui/Card';
import { Cell } from '@common/ui/Cell';
import { Loader } from '@common/ui/Loader';
import { Heading } from '@common/ui/typography/Heading';
import { useEventEmit } from '@common/utils/events';

import { loadAccountSettings, useAccountStore } from '@entities/account';

import styles from './AccountSettings.module.scss';
import { AccountSettingToggle } from './AccountSettingToggle';
import { isAccountSettingsShown } from './isAccountSettingsShown';

type AccountSheet =
  | { kind: 'datingOff'; confirm: () => void }
  | { kind: 'profileIncomplete' };

export const AccountSettings = () => {
  const navigate = useNavigate();
  const { setBottomSheet } = useTabbar();
  const { status, account, isLoading } = useAccountStore();
  const emit = useEventEmit();
  const [sheet, setSheet] = useState<AccountSheet | null>(null);
  // The two mode rows lock each other while either is saving: two racing
  // PATCHes can both pass the server's one-mode-on check.
  const [savingModeRows, setSavingModeRows] = useState(0);
  const isModeRowSaving = savingModeRows > 0;

  const trackModeRowSaving = useCallback((isPending: boolean) => {
    setSavingModeRows((count) => Math.max(0, count + (isPending ? 1 : -1)));
  }, []);

  useEffect(() => {
    if (!sheet) return;

    const closeSheet = () => setSheet(null);

    setBottomSheet(
      sheet.kind === 'datingOff' ? (
        <BottomSheet
          image="/icons/3d-icons/sad.png"
          title="Перестать знакомиться?"
          subtitle="Анкету не покажут в ленте, а лента знакомств закроется. Чаты и мэтчи останутся"
          buttons={[
            <Button
              testId="account-settings-dating-off-confirm-button"
              variant="negativeSecondary"
              onClick={() => {
                sheet.confirm();
                closeSheet();
              }}
            >
              Выключить
            </Button>,
            <Button
              testId="account-settings-dating-off-cancel-button"
              variant="actionSecondary"
              onClick={closeSheet}
            >
              Отмена
            </Button>,
          ]}
        />
      ) : (
        <BottomSheet
          image="/icons/3d-icons/like.png"
          title="Чтобы знакомиться, заполните анкету: о себе, цель и фото"
          buttons={[
            <Button
              testId="account-settings-profile-incomplete-edit-button"
              onClick={() => {
                closeSheet();
                void navigate({ to: '/profile/edit' });
              }}
            >
              Заполнить анкету
            </Button>,
            <Button
              testId="account-settings-profile-incomplete-cancel-button"
              variant="actionSecondary"
              onClick={closeSheet}
            >
              Отмена
            </Button>,
          ]}
        />
      ),
    );

    return () => setBottomSheet(null);
  }, [navigate, setBottomSheet, sheet]);

  if (!isAccountSettingsShown(status, account)) return null;

  const confirmDatingOff = (isChecked: boolean, submit: () => void) => {
    if (isChecked) {
      submit();
      return;
    }

    // Without matchmaker mode the server would refuse: say so, no sheet.
    if (!account?.matchmaker) {
      emit('error', { title: 'Оставьте включённым хотя бы один режим' });
      return;
    }

    setSheet({ kind: 'datingOff', confirm: submit });
  };

  const showProfileIncomplete = () => setSheet({ kind: 'profileIncomplete' });

  const renderContent = () => {
    if (status === 'unknown' || !account) {
      if (isLoading) {
        return (
          <div className={styles.loading}>
            <Loader />
          </div>
        );
      }

      return (
        <Cell
          testId="account-settings-unavailable"
          title="Не удалось загрузить настройки"
          suffixIcon={
            <Button
              testId="account-settings-retry-button"
              variant="actionSecondary"
              size={400}
              onClick={() => void loadAccountSettings()}
            >
              Повторить
            </Button>
          }
        />
      );
    }

    const isMatchmakerOn = account.features.matchmaker;

    return (
      <>
        <AccountSettingToggle
          testId="account-settings-dating"
          field="dating"
          title="Знакомлюсь"
          hint="Ищу пару для себя"
          hasSeparator={isMatchmakerOn}
          isLocked={isModeRowSaving}
          onPendingChange={trackModeRowSaving}
          onBeforeChange={confirmDatingOff}
          onProfileIncomplete={showProfileIncomplete}
        />
        {isMatchmakerOn && (
          <>
            <AccountSettingToggle
              testId="account-settings-matchmaker"
              field="matchmaker"
              title="Режим свахи"
              hint="Советую анкеты друзьям"
              hasSeparator
              isLocked={isModeRowSaving}
              onPendingChange={trackModeRowSaving}
            />
            <AccountSettingToggle
              testId="account-settings-hidden"
              field="hidden"
              title="Скрыть анкету из ленты"
              hint="Вас не покажут в ленте и свахам. Чаты и мэтчи останутся"
              hasSeparator
            />
            <AccountSettingToggle
              testId="account-settings-allow-recommend"
              field="allow_recommend"
              title="Разрешить советовать мою анкету"
              hint="Свахи смогут советовать вашу анкету друзьям"
            />
          </>
        )}
      </>
    );
  };

  return (
    <section className={styles.section} data-test-id="account-settings">
      <Heading level={4} style={{ padding: '5px 16px 3px' }}>
        Анкета
      </Heading>
      <Card
        testId="account-settings-card"
        style={{ padding: '0 16px', gap: 2 }}
      >
        {renderContent()}
      </Card>
    </section>
  );
};
